import React, { useEffect, useState } from "react";
import { FiAlertTriangle, FiSearch } from "react-icons/fi";
import { Pagination } from "newui/components/Pagination";
import { useAccessContacts } from "../../hooks/useAccessContacts";

interface LimitedAccessNoticeProps {
	className?: string;
}

/** Server page size for the contact list (PRO-737: five at a time). */
const PER_PAGE = 5;

const SEARCH_DEBOUNCE_MS = 300;

/**
 * PRO-737 (outcomes 5 and 6) — what a user with NO permissions sees in place of
 * the app: why they cannot do anything, and who to ask about it.
 *
 * Rendered on their profile page, which is the only surface they can reach.
 * The contact list is server-scoped to their own company and to System
 * Administrators; this component passes no company or role parameter, by
 * design — a user with no standing must not be able to widen the query.
 */
export const LimitedAccessNotice = ({ className = "" }: LimitedAccessNoticeProps) => {
	const [searchInput, setSearchInput] = useState("");
	const [search, setSearch] = useState("");
	const [page, setPage] = useState(1);

	// Debounced so typing does not fire a request per keystroke.
	useEffect(() => {
		const timer = setTimeout(() => {
			setSearch(searchInput);
			setPage(1);
		}, SEARCH_DEBOUNCE_MS);

		return () => clearTimeout(timer);
	}, [searchInput]);

	const { data, isLoading } = useAccessContacts(search, page);

	const contacts = data?.data ?? [];
	const total = data?.total ?? 0;

	return (
		<div className={className} data-testid="limited-access-notice">
			<div
				className="flex items-start gap-3 rounded-md border border-yellow-300 bg-yellow-50 p-4"
				role="status"
			>
				<FiAlertTriangle
					className="mt-0.5 shrink-0 text-yellow-600"
					size={20}
					aria-hidden="true"
				/>
				<div>
					<p className="text-sm font-medium text-yellow-800">
						Your account currently has no permissions configured and access is limited
					</p>
					<p
						className="mt-2 text-sm text-yellow-800"
						data-testid="access-contacts-message"
					>
						Please contact someone below to organise your permissions for Procuracon
					</p>
				</div>
			</div>

			<div className="mt-6">
				<div className="relative">
					<FiSearch
						className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
						size={16}
						aria-hidden="true"
					/>
					<input
						type="search"
						value={searchInput}
						onChange={(event) => setSearchInput(event.target.value)}
						placeholder="Search by name or email"
						aria-label="Search contacts by name or email"
						data-testid="access-contacts-search"
						className="w-full rounded-md border border-gray-300 py-2 pl-9 pr-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
					/>
				</div>

				<div className="mt-4 overflow-x-auto rounded-md border border-gray-200">
					<table className="w-full text-left text-sm">
						<thead className="bg-gray-50 text-xs uppercase text-gray-600">
							<tr>
								<th scope="col" className="px-4 py-3 font-semibold">Name</th>
								<th scope="col" className="px-4 py-3 font-semibold">Email</th>
							</tr>
						</thead>
						<tbody data-testid="access-contacts-table">
							{contacts.map((contact) => (
								<tr key={contact.id} className="border-t border-gray-200">
									<td className="px-4 py-3 text-gray-900">
										{`${contact.first_name} ${contact.last_name}`.trim()}
									</td>
									<td className="px-4 py-3">
										<a
											href={`mailto:${contact.email}`}
											className="text-blue-600 underline hover:text-blue-800"
										>
											{contact.email}
										</a>
									</td>
								</tr>
							))}
							{contacts.length === 0 && (
								<tr className="border-t border-gray-200">
									<td colSpan={2} className="px-4 py-6 text-center text-gray-500">
										{isLoading
											? "Loading contacts…"
											: "No contacts found. Please contact Procuracon support."}
									</td>
								</tr>
							)}
						</tbody>
					</table>

					<Pagination
						currentPage={page}
						rowsPerPage={PER_PAGE}
						totalItems={total}
						onPageChange={setPage}
						// Page size is fixed by the server at five, so the selector is
						// hidden and this handler is intentionally inert.
						onRowsPerPageChange={() => undefined}
						showRowsPerPage={false}
						testId="access-contacts-pagination"
					/>
				</div>
			</div>
		</div>
	);
};
